import type { paths } from "@/api-schema";
import {
  Action,
  BranchIdentifierSystem,
  PathsV1BranchUuidGetParametersQueryExtend,
  SubjectRoles,
} from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  BRANCH_DELETE_CUSTOM_ERRORS,
  resolveBranchDeleteErrorMessage,
} from "@/common/api/branch/_helpers/branch-delete-error";
import { useBranchByUuidQuery } from "@/common/api/branch/use-branch-by-uuid.query";
import { useBranchDeleteMutation } from "@/common/api/branch/use-branch-delete.mutation";
import { useBranchRestoreMutation } from "@/common/api/branch/use-branch-restore.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { ConfirmationType } from "@/components/confirm-dialog/_types";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailPermission, DetailTab } from "@/components/detail-page/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { getBranchIdentifier } from "@/modules/masterdata/branch/branch-identifiers";
import {
  Building2,
  Fingerprint,
  PencilIcon,
  ReceiptEuroIcon,
  ReceiptTextIcon,
  RotateCcwIcon,
  Store,
  TrashIcon,
} from "lucide-react";
import { useCallback, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { BranchDetailSkeleton } from "./_components/branch-detail-skeleton";
import { DunningTab } from "./tabs/dunning-tab/dunning-tab";
import { OpenPositionsTab } from "./tabs/open-positions-tab/open-positions-tab";
import { OverviewTab } from "./tabs/overview-tab/overview-tab";

const BRANCH_DETAIL_EXTENDS = [
  PathsV1BranchUuidGetParametersQueryExtend.Address,
  PathsV1BranchUuidGetParametersQueryExtend.Company,
  PathsV1BranchUuidGetParametersQueryExtend.Brands,
  PathsV1BranchUuidGetParametersQueryExtend.BranchIdentifiers,
  PathsV1BranchUuidGetParametersQueryExtend.BrandRelations,
  PathsV1BranchUuidGetParametersQueryExtend.BrandRelations_Brand,
  PathsV1BranchUuidGetParametersQueryExtend.DunningUser,
  PathsV1BranchUuidGetParametersQueryExtend.DunningRepresentative,
] as const;

const BRANCH_DETAIL_QUERY_PARAMS = { parameters: { extend: [...BRANCH_DETAIL_EXTENDS] } };

const BRANCH_IDENTIFIER_LABELS = [
  { system: BranchIdentifierSystem.V1, labelKey: "masterdata:V1ID" },
  { system: BranchIdentifierSystem.Formel1Ahg, labelKey: "masterdata:Formel1AhgIdentifier" },
  { system: BranchIdentifierSystem.Formel1Ghg, labelKey: "masterdata:Formel1GhgIdentifier" },
  { system: BranchIdentifierSystem.CROSS, labelKey: "masterdata:CrossIdentifier" },
  { system: BranchIdentifierSystem.FAHRSCHULE, labelKey: "masterdata:FahrschuleIdentifier" },
  { system: BranchIdentifierSystem.Sap, labelKey: "masterdata:SapIdentifier" },
  { system: BranchIdentifierSystem.CARANO, labelKey: "masterdata:CaranoIdentifier" },
] as const;

const MANAGE_BRANCH_PERMISSION: DetailPermission = { action: Action.Manage, subject: "Branch" };

const BRANCH_DETAIL_TRACKING_NAME = "branch";

export function BranchDetailView(): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";

  return (
    <Can I="read" a="Branch">
      <UuidExistenceWrapper<
        paths["/v1/branch/{uuid}"]["get"]["parameters"]["query"],
        paths["/v1/branch/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
      >
        uuid={uuid}
        query={useBranchByUuidQuery}
        notFoundProps={{
          title: t("masterdata:BranchNotFound"),
          message: t("masterdata:BranchDoesNotExist"),
          description: t("masterdata:PleaseCheckBranchIdOrContactSupport"),
          backTo: "~/masterdata/branch",
          backToLabel: t("masterdata:BackToBranchOverview"),
        }}
      >
        <BranchDetailContent uuid={uuid} />
      </UuidExistenceWrapper>
    </Can>
  );
}

function BranchDetailContent({ uuid }: { uuid: string }): ReactElement {
  const { t } = useTranslation(["masterdata", "common", "fia", "ram"]);
  const [, navigate] = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const { mutateAsync: deleteBranchMutate } = useBranchDeleteMutation();
  const { mutateAsync: restoreBranchMutate } = useBranchRestoreMutation();

  const { data: branch, isPending, isError } = useBranchByUuidQuery(uuid, BRANCH_DETAIL_QUERY_PARAMS);
  const branchLabel = branch?.name ?? t("masterdata:BranchDetails");
  const isDeleted = Boolean(branch?.deletedAt);

  const handleConfirmDelete = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => deleteBranchMutate(isDeleted ? { uuid, force: true } : { uuid }),
      successMessage: t("common:BranchHasBeenDeleted"),
      errorMessage: t("common:DeleteBranchError"),
      customErrors: BRANCH_DELETE_CUSTOM_ERRORS,
      resolveErrorMessage: resolveBranchDeleteErrorMessage,
      onSuccess: () => {
        setDeleteDialogOpen(false);
        navigate("~/masterdata/branch");
      },
    });
  }, [deleteBranchMutate, isDeleted, navigate, t, uuid]);

  const handleConfirmRestore = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => restoreBranchMutate({ uuid }),
      successMessage: t("common:BranchHasBeenRestored"),
      errorMessage: t("common:RestoreBranchError"),
      onSuccess: () => {
        setRestoreDialogOpen(false);
      },
    });
  }, [restoreBranchMutate, t, uuid]);

  if (isPending) {
    return <BranchDetailSkeleton />;
  }

  if (isError || !branch) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Default}
        title={t("masterdata:BranchDetails")}
        subTitle={t("masterdata:BranchDataCouldNotBeLoaded")}
      >
        {null}
      </CenteredLayout>
    );
  }

  const actions: DetailAction[] = isDeleted
    ? [
        {
          id: "restore",
          label: t("common:Restore"),
          icon: RotateCcwIcon,
          permission: MANAGE_BRANCH_PERMISSION,
          onClick: () => setRestoreDialogOpen(true),
        },
        {
          id: "delete-permanently",
          label: t("common:DeletePermanently"),
          icon: TrashIcon,
          permission: MANAGE_BRANCH_PERMISSION,
          destructive: true,
          onClick: () => setDeleteDialogOpen(true),
        },
      ]
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: MANAGE_BRANCH_PERMISSION,
          onClick: () => navigate(`~/masterdata/branch/${uuid}/update`),
        },
        {
          id: "delete",
          label: t("common:Delete"),
          icon: TrashIcon,
          permission: MANAGE_BRANCH_PERMISSION,
          destructive: true,
          onClick: () => setDeleteDialogOpen(true),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: "overview",
      label: t("common:Overview"),
      icon: Building2,
      content: () => <OverviewTab branch={branch} />,
    },
    {
      id: "dunning",
      label: t("masterdata:BranchDunning"),
      icon: ReceiptTextIcon,
      content: () => <DunningTab branch={branch} />,
    },
    {
      id: "open-positions",
      label: t("fia:OpenPositions"),
      icon: ReceiptEuroIcon,
      permission: { appRole: SubjectRoles.FiaUser },
      content: () => <OpenPositionsTab branchUuid={branch.uuid} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={branch.uuid}
          technicalId={branch.technicalId}
          createdAt={branch.createdAt}
          updatedAt={branch.updatedAt}
          deletedAt={branch.deletedAt}
          identifiers={BRANCH_IDENTIFIER_LABELS.map(({ system, labelKey }) => ({
            id: system,
            label: t(labelKey),
            value: getBranchIdentifier(branch, system),
          }))}
        />
      ),
    },
  ];

  return (
    <>
      <DetailPageLayout
        variant={CenteredLayoutVariant.Wide}
        header={
          <DetailHeader
            icon={<Store />}
            title={branchLabel}
            subtitle={branch.company?.name}
            badges={
              isDeleted ? (
                <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                  {t("common:Deleted")}
                </Badge>
              ) : undefined
            }
            actions={actions}
          />
        }
      >
        <DetailTabs sections={tabs} trackingName={BRANCH_DETAIL_TRACKING_NAME} />
      </DetailPageLayout>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        label={t("common:Branch")}
        title={isDeleted ? t("common:DeleteBranchPermanently") : t("common:DeleteBranch")}
        description={isDeleted ? t("common:DeleteBranchPermanentlyDescription") : t("common:DeleteBranchDescription")}
        labelConfirm={isDeleted ? t("common:YesDeletePermanently") : t("ram:YesDelete")}
        name={branchLabel}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        requireConfirmation={isDeleted}
        confirmationType={ConfirmationType.Name}
        showIrreversibleWarning={isDeleted}
      />

      <BaseConfirmDialog
        open={restoreDialogOpen}
        title={t("common:RestoreBranch")}
        description={t("common:RestoreBranchDescription")}
        labelCancel={t("common:NoCancel")}
        labelConfirm={t("common:YesRestore")}
        onCancel={() => setRestoreDialogOpen(false)}
        onConfirm={handleConfirmRestore}
      />
    </>
  );
}
